/** Boot: feature detection, UI assembly, render scheduling. */

import { darkDemo, darkDemoMinutes, onDarkDemo, stopDarkDemo } from './demo';
import { LANGS, correctionText, t, type Lang } from './i18n';
import { DARK_DEMO_MINUTES, RP_NIGHT_VISIBILITY, healthyVisibility, rpVisibility } from './optics/adaptation';
import { pupilDiameter } from './optics/eye';
import { setMaxR } from './optics/kernels';
import { buildPaneConfig } from './optics/profile';
import { retinaActive, type RetinaParams } from './optics/retina';
import { notesFor, warningsFor } from './optics/warnings';
import { Pipeline } from './render/pipeline';
import { getScene } from './scenes';
import { RENDER_H, RENDER_W } from './scenes/types';
import { choiceOf, profileOf, state, subscribe, update, type State } from './state';
import { h, clear } from './ui/dom';
import { buildOptions, buildSceneBar } from './ui/options';
import { buildPatientControls, type Controls } from './ui/patient';
import { buildReadout, type Readout } from './ui/readout';
import { buildTreatmentControls } from './ui/treatment';
import { buildViews, type Views } from './ui/views';

const root = document.getElementById('app');
if (!root) throw new Error('#app missing');

/** The WebGL canvas is not on the page: each rendered pane is copied into its own picture (D-025). */
const glCanvas = h('canvas', { width: RENDER_W, height: RENDER_H });
let pipeline: Pipeline | null = null;

function showError(message: string): void {
  clear(root!);
  root!.append(h('div', { class: 'error' }, h('p', { text: message })));
}

try {
  pipeline = new Pipeline(glCanvas);
  pipeline.measureGpu = true;
} catch (err) {
  console.error(err);
  showError(t(state.lang, 'webgl.error'));
}

/** Keys whose change needs no pane re-render (final pass, or retina + final). */
const LIGHT_KEYS: ReadonlySet<keyof State> = new Set(['gaze', 'zoom', 'tags', 'fixX', 'fixY']);

let controls: Controls[] = [];
let views: Views;
let readout: Readout;
let unsubscribe: (() => void) | null = null;
let readoutKey = '';

function buildUI(): void {
  const lang = state.lang;
  document.documentElement.lang = lang;
  document.title = t(lang, 'app.title');
  if (unsubscribe) unsubscribe();
  clear(root!);

  const patient = buildPatientControls();
  const treatment = buildTreatmentControls();
  const sceneBar = buildSceneBar();
  const options = buildOptions();
  controls = [patient, treatment, sceneBar, options];
  views = buildViews();
  readout = buildReadout(lang);
  readoutKey = '';

  const langSwitch = h('div', { class: 'lang', role: 'group' },
    ...LANGS.map((l: Lang) => h('button', {
      type: 'button', class: l === lang ? 'on' : '', 'aria-pressed': l === lang ? 'true' : 'false',
      text: t(lang, `lang.${l}` as 'lang.ru'), onclick: () => update({ lang: l }),
    })),
  );
  const header = h('header', { class: 'header' },
    h('div', {}, h('h1', { text: t(lang, 'app.title') }), h('p', { class: 'subtitle', text: t(lang, 'app.subtitle') })),
    langSwitch);
  // Grid: controls above each picture; side by side on wide screens, stacked on phones.
  const compare = h('div', { class: 'compare' },
    patient.el, treatment.el,
    h('div', { class: 'view-wrap view-wrap-now' }, views.left),
    h('div', { class: 'view-wrap view-wrap-after' }, views.right),
  );
  root!.append(h('div', { class: 'app' }, header, sceneBar.el, compare, options.el, readout.el));

  unsubscribe = subscribe((changed) => {
    if (changed.has('lang')) {
      buildUI();
      requestRender(true);
      return;
    }
    // Any real change of the eye, the treatment or the scene ends the dark-adaptation demo.
    if (darkDemo.active && [...changed].some((k) => !LIGHT_KEYS.has(k))) stopDarkDemo();
    for (const c of controls) c.sync();
    views.sync();
    const heavy = [...changed].some((k) => !LIGHT_KEYS.has(k));
    requestRender(heavy);
  });
}

onDarkDemo(() => {
  for (const c of controls) c.sync();
  views.sync();
  requestRender(false);
});

/** The first two-pane render is timed with a GPU sync; a slow GPU drops MAXR to 12 (ARCHITECTURE §5). */
const SLOW_RENDER_MS = 400;
let perfChecked = false;

function checkPerformance(paneMs: number): void {
  if (perfChecked || !pipeline) return;
  perfChecked = true;
  pipeline.measureGpu = false;
  if (paneMs > SLOW_RENDER_MS) {
    console.warn(`slow GPU: two panes took ${paneMs.toFixed(0)} ms, reducing kernel radius to 12`);
    setMaxR(12);
    pipeline.invalidate();
    requestRender(true);
  }
}

let scheduled = false;
let pendingHeavy = false;

function requestRender(heavy: boolean): void {
  pendingHeavy = pendingHeavy || heavy;
  if (scheduled) return;
  scheduled = true;
  if (pendingHeavy) {
    views.setBusy(true);
    // Two frames: let the spinner paint before the synchronous render.
    requestAnimationFrame(() => requestAnimationFrame(runRender));
  } else {
    requestAnimationFrame(runRender);
  }
}

function runRender(): void {
  scheduled = false;
  pendingHeavy = false;
  try {
    render();
  } catch (err) {
    console.error(err);
  } finally {
    views.setBusy(false);
  }
}

function render(): void {
  if (!pipeline) return;
  const scene = getScene(state.scene);
  const sceneChanged = pipeline.setScene(scene, state.lang);
  const pupil = pupilDiameter(scene.lighting, state.age);
  const profile = profileOf(state);
  const right = buildPaneConfig(profile, choiceOf(state, 'right'), pupil);
  // The left picture is always the eye without treatment; during the dark-adaptation demo both pictures
  // use the chosen treatment and only the retina differs.
  const demoT = darkDemo.active ? darkDemoMinutes() : null;
  const left = demoT === null ? buildPaneConfig(profile, { id: 'none', toric: false, monoTarget: state.monoTarget }, pupil) : right;
  const didLeft = pipeline.renderPane(0, left);
  const didRight = pipeline.renderPane(1, right);

  const night = scene.lighting === 'night';
  const base: RetinaParams = {
    glaucoma: state.glaucoma, amd: state.amd, dr: state.dr, hr: state.hr, rp: state.rp, amblyopia: state.amblyopia,
    dark: night && state.rp > 0 ? RP_NIGHT_VISIBILITY[state.rp] : 1,
    fixation: state.gaze ? [state.fixX, state.fixY] : [RENDER_W / 2, RENDER_H / 2],
  };
  let leftRetina = base;
  let rightRetina = base;
  if (demoT !== null) {
    leftRetina = { ...base, rp: 0, dark: healthyVisibility(demoT) };
    rightRetina = { ...base, dark: rpVisibility(state.rp, demoT) };
  }
  const useRetina = retinaActive(leftRetina) || retinaActive(rightRetina);
  if (useRetina) {
    pipeline.applyRetina(0, leftRetina);
    pipeline.applyRetina(1, rightRetina);
  }
  // Present each pane full-frame and copy it into its picture (split > 1: left only, < 0: right only).
  pipeline.present(2, scene.exposure, useRetina, scene.tone);
  views.blit(0, glCanvas);
  pipeline.present(-1, scene.exposure, useRetina, scene.tone);
  views.blit(1, glCanvas);
  views.setMagnification(left.magnification, right.magnification);
  views.setDemo(demoT);
  if (demoT !== null && demoT < DARK_DEMO_MINUTES) requestRender(false);

  if (sceneChanged || didLeft || didRight) {
    const tm = pipeline.timings;
    console.debug(
      `render: scene ${tm.scene.toFixed(1)} ms, left ${tm.pane[0].toFixed(1)} ms, right ${tm.pane[1].toFixed(1)} ms, present ${tm.present.toFixed(1)} ms`,
    );
    checkPerformance(tm.pane[0] + tm.pane[1]);
  }

  const lang = state.lang;
  const ctx = { aphakia: profile.aphakia };
  const afterLabel = state.rightCorrection === 'none' ? t(lang, 'treat.none') : correctionText(lang, state.rightCorrection, 'label', ctx);
  views.setAfterLabel(afterLabel);

  const key = JSON.stringify([profile, state.rightCorrection, state.monoTarget, lang]);
  if (key === readoutKey) return;
  readoutKey = key;
  const rightChoice = choiceOf(state, 'right');
  readout.setNotes(afterLabel, [
    { text: correctionText(lang, state.rightCorrection, 'desc', ctx), warning: false },
    ...notesFor(profile, rightChoice).map((k) => ({ text: t(lang, k), warning: false })),
    ...warningsFor(profile, rightChoice).map((k) => ({ text: t(lang, k), warning: true })),
  ]);
}

glCanvas.addEventListener('webglcontextlost', (e) => {
  e.preventDefault();
  console.warn('WebGL context lost');
});
glCanvas.addEventListener('webglcontextrestored', () => {
  console.warn('WebGL context restored; rebuilding');
  try {
    pipeline = new Pipeline(glCanvas);
    requestRender(true);
  } catch (err) {
    console.error(err);
    showError(t(state.lang, 'webgl.error'));
  }
});

if (pipeline) {
  buildUI();
  requestRender(true);
}

// Debug handle for the console and browser automation.
declare global {
  interface Window {
    __linza?: {
      state: State;
      update: typeof update;
      pipeline: () => Pipeline | null;
      demo: typeof darkDemo;
    };
  }
}
window.__linza = { state, update, pipeline: () => pipeline, demo: darkDemo };
